import type { AppBundle } from '../../utils/app-bundles';
import type { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import type { AccountPermission } from '@customerio/hydra-permissions';
import type { UserRole } from '@/features/roles/types';

export const VerificationState = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Unsubmitted: 'unsubmitted',
} as const;
export type VerificationState = (typeof VerificationState)[keyof typeof VerificationState];

export interface Account {
  id: number;
  created_at: number;
  name: string;
  is_sample_account_access: boolean;
  is_masq: boolean;
  write_access: boolean;
  feature_flags: Record<string, boolean>;
  logo_url: string;
  premium: boolean;
  premium_trial: boolean;
  plan_features: PlanFeature[];
  gated_features: GatedFeatures;
  admin: boolean;
  product_activations: AppBundle[];
  region: Region;
  support_tier: SupportTier;
  suspend_trial_at?: number;
  account_state: string;
  access_granted_until?: number;
  access_granted_by?: string;
  access_granted_on?: number;
  data_center: 'us' | 'eu';
  has_csm: boolean;
  cs_access_granted_until?: number;
  cs_access_granted_by?: string;
  cs_access_granted_on?: number;
  ordway_id?: string;
  read_only: boolean;
  verification_state: VerificationState;
}

export interface RawAccount {
  id: string;
  name: string;
  email: string;
  has_data: boolean;
  plan_price: number;
  plan_period_start: number;
  plan_period_end: number;
  plan_include_churn: boolean;
  next_invoice_date?: number | null;
  setup_data: number;
  setup_email: number;
  system_notification: string;
  account_state: string;
  sso_setting: string;
  confirmed: boolean;
  verification_state: VerificationState;
  admin: boolean;
  current_user_id: string;
  invoice_email: string;
  legal_email: string;
  technical_email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  logo_url: string;
  premium: boolean;
  premium_trial: boolean;
  has_csm: boolean;
  access_granted_until: number;
  access_granted_by?: string;
  access_granted_on: number;
  cs_access_granted_until: number;
  cs_access_granted_by?: string;
  cs_access_granted_on: number;
  rd_access_granted_by?: string;
  rd_access_granted_on: number;
  support_tier: SupportTier;
  ordway_id?: string;
  session_duration: number;
  environment_ids: string[];
  created_at: number;
  feature_flags: Record<string, boolean>;
  plan_flags: Record<string, boolean>;
  cancel_trial_at?: number;
  suspend_trial_at?: number;
  suspend_incomplete_subscription_at?: number;
  two_factor_authentication_required: boolean;
  delete_at?: number;
  can_reactivate: boolean;
  is_sample_account_access: boolean;
  is_masqueraded: boolean;
  max_session_duration_hours?: number;
  data_center: 'us' | 'eu';
  enable_troubleshooting_cookies: boolean;
  read_only: boolean;
  gated_features: GatedFeatures;
  product_activations?: AppBundle[];
  custom_smtp_locked: boolean;
  is_paused: boolean;
}

interface PlanFeatureInfo {
  enabled: boolean;
  showPremiumLabel: boolean;
  limit: number | false;
}

export interface AugmentedAccount extends Account {
  // active products as flags for easy access
  activeProducts: Record<AppBundle, boolean>;
  // plan features computed property
  planFeatures: Record<PlanFeature, PlanFeatureInfo>;
  // support team access enabled (computed from access_granted_until)
  support_team_access_enabled: boolean;
  // success team access enabled (computed from cs_access_granted_until)
  success_team_access_enabled: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  account_id: number;
  last_cio_workspace_id: string;
  account_permissions: AccountPermission[];
  roles: UserRole[];
  masq_permissions: UserMasqPermission;
  can_masquerade: boolean;
  can_masquerade_write: boolean;
  user_flags: { [key: string]: boolean };
}

export interface RawUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  accessible_workspace_ids: string[];
  accessible_environment_ids: string[];
  account_permissions: AccountPermission[];
  roles?: UserRole[];
  last_environment_id: string;
  created_at: number;
  last_sign_in_at: number;
  current_sign_in_at: number;
  import_max_size_bytes: number;
  two_factor_authentication_enabled?: boolean;
  two_factor_authentication_email_enabled?: boolean;
  external_identity_provider: string;
  external_identity_email: string;
  user_flags: { [key: string]: boolean };
  google_drive_credential?: { account: string };
  can_masquerade?: boolean;
  can_masquerade_write?: boolean;
  masq_permissions?: UserMasqPermission;
}

export interface AugmentedUser extends User {
  name: string; // computed property for full name
  // masq permissions computed properties
  isSuperUser: boolean;
  canReadOnlyMasq: boolean;
  canReadWriteMasq: boolean;
  noMasqAccess: boolean;
  masqLevelNotSet: boolean;
  hasReadMasqAccess: boolean;
}

export type SupportTier = 'community' | 'email' | 'chat' | '';

export enum AccountState {
  Free = 'free',
  Current = 'current',
  Canceled = 'canceled',
  Suspended = 'suspended',
  Overdue = 'overdue',
  PaymentPending = 'payment_pending',
  Deleted = 'deleted',
}

export interface PlanFeatureLimit {
  limit: number;
}

export interface GatedFeatures {
  [key: string]: PlanFeatureLimit | Record<string, never> | undefined;
}

export enum Region {
  US,
  EU,
}

export enum PlanFeature {
  Workspaces = 'workspaces',
  WarehouseSync = 'warehouse_sync',
  Collections = 'collections',
  AnonymousInApp = 'anonymous_in_app',
  RoutinesActive = 'routines_active',
  CustomSkills = 'custom_skills',
  AuditLogs = 'audit_logs',
  DeliverabilityAnalytics = 'deliverability_analytics',
}

interface IdentifiersConfig {
  cio_id: number;
  id: number;
  email: number;
}

export interface Workspace {
  id: string;
  account_id: string;
  workspace_id: string;
  name: string;
  delivery_settings: string;
  delivery_email: string;
  user_ids: string[];
  hex_code: string;
  created_at: number;
  identifier_type: string;
  identifiers?: IdentifiersConfig;
  type: string;
  stop_processing: boolean;
  email_open_tracking: boolean;
  subscription_center_enabled: boolean;
  email_delivery_disabled: boolean;
}

interface BaseSearchResult {
  doc_id: string;
  kind: string;
  title: string;
  url: string;
  other?: Record<string, any>;
}

export type CampaignType =
  | 'seg_attr'
  | 'transactional'
  | 'behavioral'
  | 'triggered_broadcast'
  | 'date'
  | 'form'
  | 'webhook'
  | 'object'
  | 'relationship'
  | 'none';

interface CampaignResult extends BaseSearchResult {
  kind: 'campaign';
  other: {
    campaign_type: CampaignType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type ActionType =
  | 'email_action'
  | 'delay_seconds_action'
  | 'delay_time_window_action'
  | 'split_randomized_action'
  | 'webhook_action'
  | 'twilio_action'
  | 'urban_airship_action'
  | 'slack_action'
  | 'attribute_update_action'
  | 'filter_match_delay_action'
  | 'grace_period_action'
  | 'push_action'
  | 'conditional_wait_action'
  | 'randomized_delay_action'
  | 'conditional_branch_action'
  | 'multi_split_branch_action'
  | 'random_cohort_branch_action'
  | 'exit_action'
  | 'static_seg_update_action'
  | 'collection_query_action'
  | 'create_event_action'
  | 'multi_lang_branch_action'
  | 'batch_update_action'
  | 'in_app_action'
  | 'trigger'
  | 'llm_action'
  | 'line_action'
  | 'inbox_action'
  | 'whatsapp_action';

interface ActionResult extends BaseSearchResult {
  kind: 'action';
  other: {
    action_type: ActionType;
    campaign_id: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    campaign_name?: string;
    campaign_url?: string;
  };
}

export type SegmentType = 'static' | 'dynamic' | 'temporary' | 'hidden';

interface SegmentResult extends BaseSearchResult {
  kind: 'segment';
  other: {
    segment_type: SegmentType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type TemplateType =
  | 'email'
  | 'webhook'
  | 'twilio'
  | 'urban_airship'
  | 'slack'
  | 'push'
  | 'create_event'
  | 'attribute_update'
  | 'batch_update_not_configured'
  | 'batch_create_event'
  | 'batch_attribute_update'
  | 'in_app'
  | 'in_app_broadcast'
  | 'llm_action_prompt'
  | 'llm_action_result_schema'
  | 'line'
  | 'inbox'
  | 'whatsapp';

interface TemplateResult extends BaseSearchResult {
  kind: 'template';
  other: {
    template_type: TemplateType;
    subject?: string;
    language?: string;
    action_id?: string;
    newsletter_id?: string;
    transactional_message_id?: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
  };
  snippets: string[];
}

interface NavigationResult extends BaseSearchResult {
  kind: 'navigation';
  title: string;
  url: string;
  description?: never; // Navigation items don't have descriptions
  other?: never; // Navigation items don't have other
  snippets?: never; // Navigation items don't have snippets
}

interface NewsletterResult extends BaseSearchResult {
  kind: 'newsletter';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    sent: string;
  };
}

export interface CustomerResult extends BaseSearchResult {
  kind: 'customer';
  other: {
    id?: string;
    email?: string;
    unsubscribed?: boolean;
    created_at?: string;
  };
}

export type SearchResult =
  | CampaignResult
  | ActionResult
  | SegmentResult
  | TemplateResult
  | NavigationResult
  | NewsletterResult
  | CustomerResult;
